























































































































import {
  defineComponent,
  ref,
  reactive,
  watch,
  computed,
  Ref
} from '@vue/composition-api'
import { useQuery, useResult } from '@vue/apollo-composable'
import appstoreNotificationsQuery from '@/lib/graphql/appstoreNotifications.query.gql'
import usersWithTransactionIdsQuery from '@/lib/graphql/usersWithTransactionIds.query.gql'
import { useRouter } from '@u3u/vue-hooks'
import UserAvatar from '@/components/UserAvatar.vue'
import MemberStatusBadge from '@/components/MemberStatusBadge.vue'
import { toDateString, numberWithCommas } from '@/lib/utils'
import moment, { Moment } from 'moment'

export default defineComponent({
  components: {
    UserAvatar,
    MemberStatusBadge
  },

  setup(_, { root }) {
    const { route, router } = useRouter()
    const pageSize = 10
    const current = +(route.value.query['current'] ?? 1)

    const parseQuery = (key: string): any => {
      return route.value.query[key]
        ? JSON.parse(route.value.query[key] as any)
        : null
    }

    const dateRange = ref<Moment[]>(
      parseQuery('dateRange')?.map((d: string) => moment(d)) ?? []
    )

    watch(dateRange, dateRange => {
      router.push({
        path: route.value.path,
        query: {
          ...route.value.query,
          dateRange: dateRange ? JSON.stringify(dateRange) : null
        }
      })
    })

    // https://developer.apple.com/documentation/appstoreservernotifications/notificationtype
    // https://developer.apple.com/documentation/appstoreservernotifications/subtype
    const notificationTypes = ref<Array<string>>(
      parseQuery('notificationTypes') ?? []
    )

    watch(notificationTypes, notificationTypes => {
      router.push({
        path: route.value.path,
        query: {
          ...route.value.query,
          notificationTypes: notificationTypes
            ? JSON.stringify(notificationTypes)
            : null
        }
      })
    })
    const notificationTypesOptions: Array<{
      value: string
      label: string
      subTypes?: Array<string>
    }> = [
      {
        value: 'CONSUMPTION_REQUEST',
        label: 'CONSUMPTION_REQUEST'
      },
      {
        value: 'DID_CHANGE_RENEWAL_PREF',
        label: 'DID_CHANGE_RENEWAL_PREF',
        subTypes: ['DOWNGRADE', 'UPGRADE']
      },
      {
        value: 'DID_CHANGE_RENEWAL_STATUS',
        label: 'DID_CHANGE_RENEWAL_STATUS',
        subTypes: ['AUTO_RENEW_DISABLED', 'AUTO_RENEW_ENABLED']
      },

      {
        value: 'DID_FAIL_TO_RENEW',
        label: 'DID_FAIL_TO_RENEW',
        subTypes: ['GRACE_PERIOD']
      },
      {
        value: 'DID_RENEW',
        label: 'DID_RENEW',
        subTypes: ['BILLING_RECOVERY']
      },
      {
        value: 'EXPIRED',
        label: 'EXPIRED',
        subTypes: [
          'BILLING_RETRY',
          'PRICE_INCREASE',
          'PRODUCT_NOT_FOR_SALE',
          'VOLUNTARY'
        ]
      },
      { value: 'GRACE_PERIOD_EXPIRED', label: 'GRACE_PERIOD_EXPIRED' },

      { value: 'OFFER_REDEEMED', label: 'OFFER_REDEEMED' },
      {
        value: 'PRICE_INCREASE',
        label: 'PRICE_INCREASE',
        subTypes: ['ACCEPTED', 'PENDING']
      },
      { value: 'REFUND', label: 'REFUND' },
      { value: 'REFUND_DECLINED', label: 'REFUND_DECLINED' },
      { value: 'REFUND_REVERSED', label: 'REFUND_REVERSED' },
      { value: 'RENEWAL_EXTENDED', label: 'RENEWAL_EXTENDED' },
      {
        value: 'RENEWAL_EXTENSION',
        label: 'RENEWAL_EXTENSION',
        subTypes: ['FAILURE', 'SUMMARY']
      },

      { value: 'REVOKE', label: 'REVOKE' },
      {
        value: 'SUBSCRIBED',
        label: 'SUBSCRIBED',
        subTypes: ['INITIAL_BUY', 'RESUBSCRIBE']
      }
    ]

    const subTypes = ref<Array<string>>(parseQuery('subTypes') ?? [])
    watch(subTypes, subTypes => {
      router.push({
        path: route.value.path,
        query: {
          ...route.value.query,
          subTypes: subTypes ? JSON.stringify(subTypes) : null
        }
      })
    })
    const subTypesOptions = computed(() => {
      if (
        notificationTypes.value != null &&
        notificationTypes.value.length > 0
      ) {
        const result = notificationTypes.value.reduce((acc, cur) => {
          const subTypes = notificationTypesOptions.find(
            entry => entry.value === cur
          )?.subTypes
          return subTypes ? [...acc, ...subTypes] : acc
        }, [] as Array<string>)
        return result.map(item => ({ value: item, label: item }))
      }
      return []
    })

    watch(notificationTypes, () => {
      subTypes.value = []
    })

    const status = ref<string | null>(
      (route.value.query['status'] as string) ?? null
    )
    watch(status, status => {
      router.push({
        path: route.value.path,
        query: {
          ...route.value.query,
          status
        }
      })
    })
    const statusOptions = [
      { value: 'expired', label: 'expired' },
      { value: 'active', label: 'active' }
    ]

    const predicateFunc = computed(() => {
      const dateFilter =
        dateRange.value.length === 2
          ? {
              // eslint-disable-next-line
              inserted_at: {
                _gte: dateRange.value[0].utc().toISOString(),
                _lte: dateRange.value[1].utc().toISOString()
              }
            }
          : {}

      const notificationTypeFilter =
        notificationTypes.value?.length > 0
          ? // eslint-disable-next-line
            { notification_type: { _in: notificationTypes.value } }
          : {}
      const subTypeFilter =
        subTypes.value?.length > 0 ? { subtype: { _in: subTypes.value } } : {}
      const statusFilter =
        status.value != null ? { status: { _eq: status.value } } : {}
      return {
        ...statusFilter,
        ...subTypeFilter,
        ...notificationTypeFilter,
        ...dateFilter
      }
    })
    const params = reactive({
      offset: (current - 1) * pageSize,
      limit: pageSize,
      where: predicateFunc
    })

    const { result, loading, onError, refetch } = useQuery(
      appstoreNotificationsQuery,
      params,
      {
        debounce: 300
      }
    )
    const data = useResult(
      result,
      { notifications: [], pagination: {} },
      data => {
        const notifications = data.app_store_notifications
        const pagination = {
          total: data.app_store_notifications_aggregate.aggregate.count,
          pageSize,
          current: params.offset / params.limit + 1
        }
        return {
          // eslint-disable-next-line
          notifications: notifications.map((u: any) => ({
            ...u,
            insertedAt: toDateString(new Date(u.inserted_at))
          })),
          pagination
        }
      }
    )
    const transactionIds = computed(() => {
      return data?.value.notifications.map((notif: any) => notif.transaction_id)
    })
    const usersParams = computed(() => ({
      transactionIds: transactionIds.value
    }))

    const { result: usersResult } = useQuery(
      usersWithTransactionIdsQuery,
      usersParams,
      () => ({
        enabled: transactionIds.value.length > 0
      })
    )
    const usersData = useResult(usersResult)
    const transactionUserMapping = computed(() => {
      if (usersData.value) {
        return Object.fromEntries(
          usersData.value.map((d: any) => [d.transaction_id, d.user])
        )
      }
      return {}
    })

    onError(error => {
      console.error(error)
      root.$notification.error({
        message: 'Error',
        description: 'An error occurred'
      })
    })

    const columns = [
      {
        title: '',
        scopedSlots: { customRender: 'avatar' },
        align: 'center'
      },
      {
        title: 'id',
        scopedSlots: { customRender: 'userId' }
      },
      {
        title: 'name',
        scopedSlots: { customRender: 'name' }
      },
      {
        title: 'email',
        scopedSlots: { customRender: 'email' }
      },
      {
        title: 'notification_type',
        dataIndex: 'notification_type'
      },
      {
        title: 'subtype',
        dataIndex: 'subtype'
      },
      {
        title: 'status',
        dataIndex: 'status',
        scopedSlots: { customRender: 'status' }
      },
      {
        title: 'transaction_id',
        dataIndex: 'transaction_id'
      },
      {
        title: 'created at',
        dataIndex: 'insertedAt'
      }
    ]

    const handleTableChange = ({
      pageSize,
      current
    }: {
      pageSize: number
      current: number
    }) =>
      // filters: any,
      // sorter: { field: string; order: string }
      {
        router.push({
          path: route.value.path,
          query: { ...route.value.query, current: `${current}` }
        })
        params.offset = (current - 1) * pageSize
        refetch()
      }

    const record: Ref<null | any> = ref(null)
    const modalTitle = computed(() => {
      return `${record.value?.user.name} - ${record.value?.notification.subtype}`
    })
    const modalVisible = ref(false)
    // eslint-disable-next-line
    const showRecord = (transactionId: string) => {
      const notif = data.value.notifications.find(
        (notif: any) => notif.transaction_id === transactionId
      )
      record.value = {
        user: transactionUserMapping.value[transactionId],
        notification: notif
      }
      modalVisible.value = true
    }
    const hideRecord = () => {
      record.value = null
    }

    return {
      record,
      modalTitle,
      modalVisible,
      showRecord,
      hideRecord,

      handleTableChange,

      numberWithCommas,

      loading,
      data,
      columns,

      notificationTypes,
      notificationTypesOptions,

      subTypes,
      subTypesOptions,

      status,
      statusOptions,

      dateRange,
      transactionUserMapping
    }
  }
})
